import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Mail, Phone, User, MessageSquare, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const ContactForm = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData,
      });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: t('contactForm.messageSent'),
          description: t('contact.messageSuccess'),
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        throw new Error(data?.error || t('contact.messageError'));
      }
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: t('contact.messageError'),
        description: t('contact.messageError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-brand-gray border-none text-white max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl md:text-3xl font-bold text-center">{t('contact.title')}</CardTitle>
        <CardDescription className="text-gray-400 text-center">
          {t('contact.subtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">
                {t('contactForm.name')} {t('contactForm.required')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={t('contact.fullName')}
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-brand-darker border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">
                {t('contactForm.email')} {t('contactForm.required')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('auth.yourEmail')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-brand-darker border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-gray-300">
                {t('contactForm.phone')}
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+351 912 345 678"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-10 bg-brand-darker border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject" className="text-gray-300">
                {t('contactForm.subject')}
              </Label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder={t('contactForm.subjectPlaceholder')}
                  value={formData.subject}
                  onChange={handleChange}
                  className="pl-10 bg-brand-darker border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="text-gray-300">
              {t('contactForm.message')} {t('contactForm.required')}
            </Label>
            <Textarea
              id="message"
              name="message"
              placeholder={t('contact.message')}
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              className="bg-brand-darker border-gray-700 text-white placeholder:text-gray-500 resize-none"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-red hover:bg-brand-red/90 text-white font-semibold py-6 text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <Mail className="mr-2 h-5 w-5" />
                {t('contactForm.sendMessage')}
              </>
            )}
          </Button>

          <p className="text-xs text-gray-400 text-center">
            {t('contactForm.required')} {t('contact.required')}. {t('contact.privacyNote')}
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;


